import {DateTime} from 'luxon';

import * as intervals from '@explorer/util/intervals';

export const getLabel = period => {
  let label;
  const startDate = DateTime.fromISO(period.first_acquired, {zone: 'utc'});
  const endDate = DateTime.fromISO(period.last_acquired, {zone: 'utc'});

  const formatInterval =
    period.interval === intervals.BIWEEK ? intervals.WEEK : period.interval;

  switch (formatInterval) {
    case intervals.QUARTER: {
      const finalDate = endDate.minus({months: 1});
      label = `${startDate.toFormat('MMM')} - ${finalDate.toFormat('MMM')}`;
      break;
    }
    case intervals.MONTH: {
      label = startDate.toFormat('MMM');
      break;
    }
    case intervals.DAY: {
      label = startDate.toFormat('dd');
      break;
    }
    case intervals.WEEK: {
      label = `${startDate.toFormat('dd')} - ${endDate
        .minus({days: 1})
        .toFormat('dd')}`;
      break;
    }
    default: {
      throw new Error(`Unexpected interval '${formatInterval}'`);
    }
  }
  return label;
};

/**
 * Generate a string key given a day.
 * @param {Date} date A date object or serialized date object.
 * @return {string} A string key for the day.
 */
const keyForDay = date =>
  DateTime.fromISO(date, {zone: 'utc'}).toFormat('MM/dd/yyyy');

export const calcDailyPeriods = (results, selectedResults) => {
  let startDate = DateTime.utc();
  let endDate = DateTime.utc().endOf('day');

  if (results.length === 0) {
    startDate.minus({months: 6});
  } else {
    startDate = DateTime.fromISO(
      results[results.length - 1].properties.acquired,
      {zone: 'utc'}
    );
    endDate = DateTime.fromISO(results[0].properties.acquired, {
      zone: 'utc',
    }).endOf('day');
  }

  const selectedDates = {};

  selectedResults.forEach(result => {
    const acquired = result.properties.acquired;
    selectedDates[keyForDay(acquired)] = true;
  });

  const datesWithResults = {};

  results.length &&
    results.forEach(result => {
      const acquired = result.properties.acquired;
      datesWithResults[keyForDay(acquired)] = true;
    });

  const days = [];

  // normalize to account for sorting
  let start = startDate < endDate ? startDate : endDate;
  const end = startDate < endDate ? endDate : startDate;

  while (start < end) {
    const iso = start.toISO();
    const currentDayKey = keyForDay(start);

    days.push({
      key: currentDayKey,
      interval: intervals.DAY,
      first_acquired: iso,
      last_acquired: iso,
      hasResult: datesWithResults[currentDayKey],
      selected: selectedDates[currentDayKey],
      product_type: 'daily',
    });

    start = start.plus({days: 1});
  }

  return days;
};

export const getPeriodWidth = interval => {
  const defaultSpace = 60;
  switch (interval) {
    case intervals.QUARTER:
      return defaultSpace * 1.5;
    case intervals.DAY:
      return defaultSpace / 2;
    default:
      return defaultSpace;
  }
};

const getKeyFunc = series => {
  let keyFunc = date => {
    return DateTime.fromISO(date, {zone: 'utc'}).toFormat('yyyy-MM');
  };

  if (
    series.interval &&
    (series.interval.indexOf('week') > 0 || series.interval.indexOf('days') > 0)
  ) {
    keyFunc = date => DateTime.fromISO(date, {zone: 'utc'}).toFormat('yyyy-WW');
  }
  return keyFunc;
};

export const calcBasemapPeriods = (series, mosaics, selectedMosaic) => {
  let firstMoment = DateTime.fromISO(series.first_acquired, {zone: 'utc'});
  const lastMoment = DateTime.fromISO(series.last_acquired, {zone: 'utc'});

  let incUnits;
  let incCount;
  const keyFunc = getKeyFunc(series);

  const interval = series.interval.split(' ');
  if (series.interval.indexOf('mon') > 0) {
    incCount = parseInt(interval[0]);
    incUnits = 'months';
    if (incCount === 1) {
      firstMoment = firstMoment.startOf('month');
    } else if (incCount === 3) {
      firstMoment = firstMoment.startOf('quarter');
    }
  } else if (
    series.interval.indexOf('week') > 0 ||
    series.interval.indexOf('days') > 0
  ) {
    incCount = Math.ceil(parseInt(interval[0]) / 7);
    incUnits = 'weeks';
    firstMoment = firstMoment;
  }
  // sort the mosaics by start date.
  const mosaicsByDate = {};
  for (let i = 0, ii = mosaics.length; i < ii; i++) {
    const m = mosaics[i];
    mosaicsByDate[keyFunc(m.first_acquired)] = m;
  }

  const periods = [];

  let start = firstMoment;

  while (start <= lastMoment) {
    const firstDate = start.toISO();
    const dateKey = keyFunc(firstDate);
    const mosaic = mosaicsByDate[dateKey];
    const available = mosaic !== undefined;
    const hasResult = available && mosaic.hasAccess;

    start = start.plus({[incUnits]: incCount});

    periods.push({
      ...mosaic,
      key: dateKey,
      first_acquired: firstDate,
      last_acquired: start.toISO(),
      interval: series.interval,
      available,
      hasResult,
      selected: mosaic && selectedMosaic?.id === mosaic.id,
    });
  }

  return periods;
};

export const isBasemap = (period, interval) =>
  (interval === intervals.WEEK ||
    interval === intervals.BIWEEK ||
    interval === intervals.QUARTER ||
    interval === intervals.MONTH) &&
  period.product_type === 'timelapse';
